import { MenuItem } from '@mui/material';
import { DateTime } from 'luxon';
import LanguageContext from 'providers/i18n/LanguageContext';
import { useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { RoundedSelect } from 'styles/styles';

import { CapitalizedMenuItem } from './styles';

interface ReportTimeRangePickerProps {
  onChange: (time: string) => void
  selectedTime: string
  startTime: DateTime
  timezone?: string
}

function ReportTimeRangePicker(props: ReportTimeRangePickerProps) {
  const {
    onChange,
    selectedTime,
    startTime,
    timezone = 'local',
  } = props;

  const { language } = useContext(LanguageContext);

  const months = useMemo(() => {
    const diff = startTime.setZone(timezone).startOf('month').diffNow('months');
    const monthsSinceStart = Math.ceil(Math.abs(diff.months));
    const monthStart = startTime.setZone(timezone).startOf('month');
    return Array.from({ length: monthsSinceStart }).map((_, index) => {
      const time = monthStart.plus({ months: index });
      return {
        time: time.toISO({ includeOffset: true })!,
        formatted: time.toLocaleString({ month: 'long', year: 'numeric' }, { locale: language }),
      };
    }).reverse();
  }, [language, startTime, timezone]);

  return (
    <RoundedSelect
      value={selectedTime}
      onChange={(event) => onChange(event.target.value as string)}
    >
      <MenuItem value="preset#all">
        <FormattedMessage id="filter_all" />
      </MenuItem>
      <MenuItem value="preset#today">
        <FormattedMessage id="filter_today" />
      </MenuItem>
      <MenuItem value="preset#yesterday">
        <FormattedMessage id="filter_yesterday" />
      </MenuItem>
      <MenuItem value="preset#last_week">
        <FormattedMessage id="filter_last_week" />
      </MenuItem>
      {months.map(({ formatted, time }, index) => (
        <CapitalizedMenuItem key={`month#${time}`} value={`month#${time}`}>
          {index === 0 ? (
            <FormattedMessage id="filter_this_month" />
          ) : (
            formatted
          )}
        </CapitalizedMenuItem>
      ))}
    </RoundedSelect>
  );
}

export default ReportTimeRangePicker;
